import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Brush,
} from "recharts";
import "./ComboChart.css";

const ComboChart = ({
  data,
  showIncome,
  showJobs,
  completedOnly,
  brushStartIndex,
  brushEndIndex,
  onBrushChange,
}) => {
  // Format data for Recharts - memoized to avoid recalculation
  const chartData = useMemo(() => {
    return data.map((item) => ({
      month: item.period_label || item.period,
      fullLabel: item.period_label,
      income: item.total_income,
      jobs: completedOnly ? item.total_jobs : item.completed_jobs,
      avgIncome: item.avg_income,
      incomplete_income: !completedOnly ? item.incomplete_income || 0 : 0,
      incomplete_jobs: !completedOnly ? item.incomplete_jobs || 0 : 0,
    }));
  }, [data, completedOnly]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const totalIncome = data.income + (data.incomplete_income || 0);
      const totalJobs = data.jobs + (data.incomplete_jobs || 0);
      const hasIncomplete =
        !completedOnly &&
        (data.incomplete_income > 0 || data.incomplete_jobs > 0);

      return (
        <div className="combo-chart-tooltip">
          <p className="tooltip-label">{data.fullLabel}</p>
          <p className="tooltip-income">
            <span className="tooltip-label-text">Income</span>
            <span className="tooltip-value-text">
              {hasIncomplete && <sup className="tooltip-estimate">est.</sup>}$
              {totalIncome?.toLocaleString()}
            </span>
          </p>
          <p className="tooltip-jobs">
            <span className="tooltip-label-text">Jobs</span>
            <span className="tooltip-value-text">
              {hasIncomplete && <sup className="tooltip-estimate">est.</sup>}
              {totalJobs} jobs
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Show message when no data types are selected
  if (!showIncome && !showJobs) {
    return (
      <div className="combo-chart-container">
        <div
          style={{
            height: 280,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6C757D",
            fontSize: 16,
            fontWeight: 500,
          }}
        >
          Select at least one data type to display
        </div>
      </div>
    );
  }

  return (
    <div className="combo-chart-container">
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            bottom: 20,
            left: 20,
          }}
        >
          <XAxis
            dataKey="month"
            axisLine={{ stroke: "#ADB5BD" }}
            tickLine={false}
            tick={{ fontSize: 14, fontWeight: 400, fill: "#6C757D" }}
          />
          <YAxis
            yAxisId="left"
            orientation="left"
            axisLine={{ stroke: "#ADB5BD" }}
            tickLine={false}
            tick={{ fontSize: 14, fontWeight: 400, fill: "#6C757D" }}
            tickFormatter={(value) => `$${value.toLocaleString()}`}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            axisLine={{ stroke: "#ADB5BD" }}
            tickLine={false}
            tick={
              showJobs
                ? { fontSize: 14, fontWeight: 400, fill: "#6C757D" }
                : false
            }
            tickFormatter={showJobs ? (value) => `${value} jobs` : undefined}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ strokeDasharray: "3 3" }}
          />

          {showJobs && (
            <>
              <Bar
                yAxisId="right"
                dataKey="jobs"
                stackId="jobs"
                fill="#075AD8"
                name="Jobs"
                barSize={32}
                radius={completedOnly ? [2, 2, 0, 0] : [0, 0, 0, 0]}
              />
              {!completedOnly && (
                <Bar
                  yAxisId="right"
                  dataKey="incomplete_jobs"
                  stackId="jobs"
                  fill="#ADB5BD"
                  name="Incomplete Jobs"
                  barSize={32}
                  radius={[2, 2, 0, 0]}
                />
              )}
            </>
          )}

          {/* Invisible Bar for consistent layout when only Income is shown */}
          {!showJobs && showIncome && (
            <Bar
              yAxisId="right"
              dataKey="income"
              fill="transparent"
              barSize={32}
            />
          )}

          {showIncome && (
            <Line
              yAxisId="left"
              type="linear"
              dataKey="income"
              stroke="#DC3545"
              strokeWidth={2}
              strokeDasharray={!completedOnly ? "5 5" : "0"}
              name="Income"
              dot={{
                fill: "#FFFFFF",
                stroke: "#DC3545",
                strokeWidth: 2,
                r: 4,
                strokeDasharray: "0",
              }}
              activeDot={{
                r: 6,
                stroke: "#DC3545",
                strokeWidth: 2,
                fill: "#FFFFFF",
                strokeDasharray: "0",
              }}
            />
          )}
          <Brush
            dataKey="month"
            height={30}
            stroke="#0D6EFD"
            startIndex={brushStartIndex}
            endIndex={brushEndIndex}
            onChange={onBrushChange}
            alwaysShowText={true}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="combo-chart-note">
        {completedOnly
          ? "*Only completed jobs are included"
          : "*Incomplete jobs are included"}
      </div>
    </div>
  );
};

ComboChart.propTypes = {
  data: PropTypes.array.isRequired,
  showIncome: PropTypes.bool.isRequired,
  showJobs: PropTypes.bool.isRequired,
  completedOnly: PropTypes.bool.isRequired,
  brushStartIndex: PropTypes.number.isRequired,
  brushEndIndex: PropTypes.number.isRequired,
  onBrushChange: PropTypes.func.isRequired,
};

export default ComboChart;
